import React, { useCallback, useEffect, useState } from 'react';
import Card from 'components/inputs/Card';
import { Spinner, Modal } from 'react-bootstrap';
import Button from 'components/inputs/Button';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { UPDATE_USER } from 'actions/user';
import { useParams } from 'react-router';
import { UPDATE_STATUS_CODE } from 'components/common/constant';
import { toast } from 'react-toastify';
import { FETCH_CUSTOMER_PROFILE } from 'actions/customer';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import CheckBox from 'components/inputs/CheckBox';
import RequestModal from '../partnerManagement/EMSP/RequestModal';
import AddPrivateStationModal from './AddPrivateStationModal';

const FleetStationAccess = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { fleetId } = useParams();

  const [showStationModel, setShowStationModel] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [stationSelection, setStationSelection] = useState({});
  const [showAddPrivateModal, setShowAddPrivateModal] = useState(false);

  const userDetail = useSelector((state) => state.customerProfile.customerProfile);
  const isLoader = useSelector((state) => state.customerProfile.isLoading);
  const [stationList, setStationList] = useState([]);

  // Sync local list whenever the redux customer doc changes (initial load + manual refetch).
  useEffect(() => {
    setStationList(userDetail?.allowed_private_stations || []);
  }, [userDetail]);

  const getCustomerDetail = useCallback(
    (id) => {
      dispatch({ type: FETCH_CUSTOMER_PROFILE.REQUEST, payload: id });
    },
    [dispatch]
  );

  useEffect(() => {
    getCustomerDetail(fleetId);
  }, [fleetId, getCustomerDetail]);

  const persistAccessList = useCallback(
    (nextStations, successMessage) => {
      const ids = _.map(nextStations, (s) => (s?.id ? s.id : s));
      dispatch({
        type: UPDATE_USER.REQUEST,
        payload: { allowed_private_stations: ids, id: fleetId },
        cb: (res) => {
          if (_.get(res, 'status') === UPDATE_STATUS_CODE) {
            toast.success(successMessage);
            setStationSelection({});
            setSelectAll(false);
            // refresh redux in background; UI already reflects new list
            getCustomerDetail(fleetId);
          }
        },
      });
    },
    [dispatch, fleetId, getCustomerDetail]
  );

  useEffect(() => {
    setSelectAll(false);
    const shouldSelectAll = stationList?.every((station) => {
      const selection = stationSelection[station.id];
      return selection?.stationChecked === true;
    });
    setSelectAll(shouldSelectAll);
  }, [stationList, stationSelection]);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    const updatedSelection = {};
    stationList.forEach((station) => {
      updatedSelection[station.id] = {
        stationChecked: newSelectAll,
      };
    });

    setStationSelection((prev) => ({ ...prev, ...updatedSelection }));
  };

  const handleStationSelect = (stationId) => {
    const stationChecked = !stationSelection[stationId]?.stationChecked;

    setStationSelection((prev) => {
      const updatedSelection = {
        ...prev,
        [stationId]: {
          stationChecked,
        },
      };
      updateSelectAll(updatedSelection);
      return updatedSelection;
    });
  };

  const updateSelectAll = (updatedSelection) => {
    const allChecked = stationList.every((station) => {
      const selection = updatedSelection[station.id];
      return selection?.stationChecked === true;
    });
    setSelectAll(allChecked);
  };

  const handleCloseStationModel = () => {
    setShowStationModel(false);
  };

  const getSelectedStationIds = () => {
    return Object.entries(stationSelection)
      .filter((entry) => entry[1].stationChecked)
      .map((entry) => entry[0]);
  };

  const isAnyStationSelected = Object.values(stationSelection).some((selection) => selection.stationChecked);

  const handleAddPrivateStations = (newStations) => {
    setStationList((prev) => {
      const existingIds = new Set(prev.map((s) => s.id));
      const additions = newStations.filter((s) => !existingIds.has(s.id));
      const next = [...prev, ...additions];
      persistAccessList(next, 'Private station(s) granted');
      return next;
    });
    setShowAddPrivateModal(false);
  };

  return (
    <React.Fragment>
      <div className="fsa-tab">
      <Card>
        <div className="fsa__toolbar">
          <span className="fsa__count">
            {`Total Stations: ${stationList.length}`}
          </span>
          {!_.isEmpty(stationList) && !(isLoader || _.isUndefined(isLoader)) && (
            <div className="fsa__select-all">
              <label>{t('stationAccessToPatner.selectAll')}</label>
              <CheckBox checked={selectAll} onChange={handleSelectAll} />
            </div>
          )}
          <Button onClick={() => setShowAddPrivateModal(true)} className="fsa__add-btn">
            {'+ Add Private Station'}
          </Button>
        </div>
        <div className="station-access-list__main">
          <div className="station-access-list--block">
            {isLoader ? (
              <div className="loader--block">
                <Spinner as="span" animation="border" size="sm" role="status" />
                <span className="ps-2">{t('placeHolder.loading')}</span>
              </div>
            ) : _.isEmpty(stationList) ? (
              <div className="empty-data-block">{t('stationAccessToPatner.noData')}</div>
            ) : (
              _.map(stationList, (item) => (
                <ChargerDataItem
                  key={item.id}
                  station={item}
                  stationChecked={stationSelection[item.id]?.stationChecked || false}
                  onStationSelect={() => handleStationSelect(item.id)}
                />
              ))
            )}
          </div>
        </div>
        {!_.isEmpty(stationList) && !(isLoader || _.isUndefined(isLoader)) && (
          <div className="charger-list--button fsa__remove-row">
            <Button
              onClick={() => setShowStationModel(true)}
              disabled={!isAnyStationSelected}
              className="fsa__remove-btn"
            >
              {t('button.remove')}
            </Button>
          </div>
        )}
      </Card>
      </div>

      <AddPrivateStationModal
        show={showAddPrivateModal}
        onClose={() => setShowAddPrivateModal(false)}
        onSubmit={handleAddPrivateStations}
      />

      {showStationModel && (
        <Modal show={showStationModel} centered onHide={handleCloseStationModel}>
          <RequestModal
            subTitle={t('stationAccessToPatner.modelTitle')}
            title={t('stationAccessToPatner.modelTitle')}
            onClose={handleCloseStationModel}
            requestModal={() => {
              const selectedIds = getSelectedStationIds();
              const remaining = stationList.filter((s) => !selectedIds.includes(s.id));
              setStationList(remaining);
              persistAccessList(remaining, 'Remove Station(s) successfully');
              setShowStationModel(false);
            }}
          />
        </Modal>
      )}
    </React.Fragment>
  );
};

const formatLocation = (station) => {
  const address = (station.address || '').trim();
  if (address) return address;
  return (
    [station.area, station.city, station.state, station.pincode]
      .map((v) => (v == null ? '' : String(v)).trim())
      .filter(Boolean)
      .join(', ') || '—'
  );
};

const ChargerDataItem = ({ station, stationChecked, onStationSelect }) => {
  return (
    <div className="station-access--list" onClick={onStationSelect}>
      <div className="list-info">
        <div className="station--label">{station.name || '—'}</div>
        <div className="charger--label">{formatLocation(station)}</div>
      </div>
      <CheckBox checked={stationChecked} onChange={onStationSelect} />
    </div>
  );
};

ChargerDataItem.propTypes = {
  station: PropTypes.object,
  onStationSelect: PropTypes.func,
  stationChecked: PropTypes.bool,
};

export default FleetStationAccess;
